import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import BootstrapCard from "../../Components/CarouselCard/";
import Header from "../../Components/Header";
import WhatsAppIcon from "../../Components/wtsapicon/WhatsAppIcon";
import ColorBG from "../../Components/Overlay";
import "./test.css";

const Test = () => {
    const [isNavigate, setIsNavigate] = useState(false);
    const [services, setServices] = useState([]);
    const [backgroundVideo, setBackgroundVideo] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);

    const videoRef = useRef(null);
    const progressBarRef = useRef(null);

    const toggleNavigate = () => setIsNavigate((prev) => !prev);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = 0.5;
        }

        const fetchData = async () => {
            try {
                const [serviceRes, videoRes] = await Promise.all([
                    axios.get("https://api.glyphqai.com/api/get-servicetype"),
                    axios.get("https://api.glyphqai.com/api/get-backgroundvideo"),
                ]);
                setServices(serviceRes.data.service_type);
                setBackgroundVideo(videoRes.data.backgroundvideo[0].source);
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        fetchData();
    }, []);

    const itemCount = services.length;

    useEffect(() => {
        if (itemCount > 0) setActiveIndex(Math.floor(itemCount / 2));
    }, [itemCount]);

    const scrollTimeoutRef = useRef(null);
    const handleWheel = useCallback(
        (event) => {
            event.preventDefault();
            if (scrollTimeoutRef.current) return;

            scrollTimeoutRef.current = setTimeout(() => {
                setActiveIndex((prevIndex) => {
                    if (event.deltaY > 0) return (prevIndex + 1) % itemCount;
                    if (event.deltaY < 0) return (prevIndex - 1 + itemCount) % itemCount;
                    return prevIndex;
                });
                scrollTimeoutRef.current = null;
            }, 500);
        },
        [itemCount]
    );

    useEffect(() => {
        window.addEventListener("wheel", handleWheel);
        return () => window.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);

    const responsiveSpacing = useMemo(() => {
        const width = window.innerWidth;
        if (width < 576) return { base: 50, factor: 100, offset: 180, visible: 2 };
        if (width < 768) return { base: 10, factor: 210, offset: 180, visible: 2 };
        if (width < 992) return { base: 50, factor: 200, offset: 180, visible: 4 };
        if (width < 1200) return { base: 100, factor: 250, offset: 180, visible: 6 };
        if (width < 1440) return { base: 250, factor: 100, offset: 180, visible: 8 };
        if (width < 1550) return { base: 212, factor: 250, offset: 180, visible: 8 };
        if (width < 1600) return { base: 225, factor: 220, offset: 180, visible: 8 };
        if (width < 1920) return { base: 450, factor: 150, offset: 180, visible: 10 };
        if (width < 2559) return { base: 250, factor: 300, offset: 180, visible: 8 };
        return { base: 200, factor: 350, offset: 180, visible: 8 };
    }, []);

    const getItemStyle = (index) => {
        const { base, factor, offset, visible } = responsiveSpacing;
        let position = index - activeIndex;
        // Normalize position to handle circularity
        const half = Math.floor(itemCount / 2);
        if (position < -half) position += itemCount;
        if (position > half) position -= itemCount;

        const isVisible = Math.abs(position) <= Math.floor(visible / 2);

        return {
            display: isVisible ? "block" : "none",
            left: `calc(50% - ${offset}px + ${position * (factor + base)}px)`,
            transform: `translateZ(${-Math.abs(position) * 450}px) translateY(-50%)`,
            zIndex: -Math.abs(position),
            opacity: isVisible ? 1 : 0,
            transition: "all 0.3s linear",
            top: "50%",
        };
    };

    // Click handler for cards
    const handleCardClick = (index) => {
        setActiveIndex(index);
        // Optionally update progress bar style via ref if needed
        if (progressBarRef.current) {
            progressBarRef.current.style.setProperty(
                "--pos-cursor",
                `${(index / (itemCount - 1)) * 100}%`
            );
        }
    };

    // Simplified progress bar click (touch/drag logic removed for brevity)
    const handleProgressBarClick = (e) => {
        const progressBar = progressBarRef.current;
        if (!progressBar) return;
        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newIndex = Math.floor((clickX / rect.width) * itemCount);
        setActiveIndex(newIndex);
    };

    return (
        <>
            <Helmet>
                <title>Home Page - Services</title>
                <meta
                    name="description"
                    content="Explore our wide range of services including app development, web development, content writing, SEO, and voiceover services. Discover how we can help elevate your business."
                />
                {/* Other meta tags omitted for brevity */}
            </Helmet>

            <div className="video-background">
                <video
                    ref={videoRef}
                    src={`https://api.glyphqai.com/uploads/backgroundvideo/${backgroundVideo}`}
                    autoPlay
                    muted
                    loop
                >
                    Your browser does not support the video tag.
                </video>
            </div>

            <Header />

            <div className="position-relative overflow-hidden">
                <div
                    className="slider mt-auto overflow-hidden"
                    style={{ transform: "translateY(10%)" }}
                >
                    {services.map((data, index) => (
                        <div
                            key={index}
                            className="item"
                            style={getItemStyle(index)}
                            onClick={() => handleCardClick(index)}
                        >
                            <BootstrapCard
                                backgroundImage={`https://api.glyphqai.com/uploads/servicetype/${data.image}`}
                                btntext={data.title}
                                buttonLink={`Service/${data.id}`}
                                extraClass="custom-class"
                                isActive={index === activeIndex}
                                service={data}
                                update={toggleNavigate}
                            />
                        </div>
                    ))}
                </div>

                <WhatsAppIcon />

                <div
                    ref={progressBarRef}
                    className="progress-bar-container__"
                    onClick={handleProgressBarClick}
                >
                    <div
                        className="position-relative h-100 mx-auto"
                        style={{ width: "95%" }}
                    >
                        <div
                            className="draggable-circle__"
                            style={{
                                left: `${(activeIndex / (itemCount - 1)) * 100}%`,
                            }}
                        ></div>
                    </div>
                </div>
            </div>

            <ColorBG
                style={{
                    top: isNavigate ? "0%" : "150%",
                    transform: isNavigate ? "rotate(0deg)" : "rotate(10deg)",
                }}
            />
        </>
    );
};

export default Test;
